import { useEffect, useRef, useState } from "react";
import { LoaderCircle, LockKeyhole, Mail, X } from "lucide-react";

type AuthDialogProps = {
  open: boolean;
  onClose: () => void;
  onEmailSignIn: (email: string, password: string) => Promise<unknown>;
  onCreateAccount: (email: string, password: string) => Promise<unknown>;
};

function authMessage(error: unknown): string {
  const code =
    typeof error === "object" && error && "code" in error
      ? String((error as { code: unknown }).code)
      : "";

  if (code.includes("invalid-credential")) return "E-posta veya şifre hatalı.";
  if (code.includes("email-already-in-use")) return "Bu e-posta zaten kayıtlı.";
  if (code.includes("weak-password")) return "Şifre en az 6 karakter olmalı.";
  if (code.includes("unauthorized-domain")) {
    return "Bu alan adı Firebase yetkili alan adlarına eklenmemiş.";
  }
  return "Giriş işlemi tamamlanamadı. Biraz sonra tekrar dene.";
}

export function AuthDialog({
  open,
  onClose,
  onEmailSignIn,
  onCreateAccount,
}: AuthDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError("");
    try {
      await action();
      onClose();
    } catch (reason) {
      setError(authMessage(reason));
    } finally {
      setBusy(false);
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="auth-dialog"
      onCancel={onClose}
      onClose={onClose}
    >
      <div className="auth-card">
        <button className="icon-button auth-close" onClick={onClose} aria-label="Kapat">
          <X size={20} />
        </button>
        <div className="auth-mark">VT</div>
        <p className="eyebrow">KİŞİSEL ÖĞRENME ALANIN</p>
        <h2>{mode === "signin" ? "Tekrar hoş geldin" : "Hesabını oluştur"}</h2>
        <p className="muted">
          Sözlüğünü hesabına kaydet; kitap arşivin bu cihazda özel kalsın.
        </p>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            void run(() =>
              mode === "signin"
                ? onEmailSignIn(email, password)
                : onCreateAccount(email, password),
            );
          }}
        >
          <label>
            <span>E-posta</span>
            <span className="input-shell">
              <Mail size={18} />
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
                placeholder="sen@email.com"
              />
            </span>
          </label>
          <label>
            <span>Şifre</span>
            <span className="input-shell">
              <LockKeyhole size={18} />
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
                minLength={6}
                required
                placeholder="En az 6 karakter"
              />
            </span>
          </label>

          {error && <p className="form-error" role="alert">{error}</p>}

          <button className="primary-button auth-submit" disabled={busy}>
            {busy && <LoaderCircle className="spin" size={18} />}
            {mode === "signin" ? "Giriş yap" : "Ücretsiz hesap oluştur"}
          </button>
        </form>

        <button
          className="text-button auth-switch"
          onClick={() => {
            setMode(mode === "signin" ? "signup" : "signin");
            setError("");
          }}
        >
          {mode === "signin"
            ? "Hesabın yok mu? Kayıt ol"
            : "Zaten hesabın var mı? Giriş yap"}
        </button>
      </div>
    </dialog>
  );
}
